<template>
  <div id="app">
    <router-view />
    <v-notification-dialog
      ref="notificationDialog"
      :type="dialogType"
      :title="dialogTitle"
      :message="dialogMessage"
    />
  </div>
</template>

<script>
import VNotificationDialog from '@/components/common/VNotificationDialog.vue'
import * as types from '@/store/types'
import { templates, createHtmlMessageFromList } from '@/utils/notification'

export default {
  provide() {
    return {
      createNotification: this.createNotification,
      createNotificationFromTemplate: this.createNotificationFromTemplate
    }
  },

  components: {
    VNotificationDialog
  },

  data() {
    return {
      loadingInstance: null,
      dialogType: '',
      dialogTitle: '',
      dialogMessage: ''
    }
  },

  computed: {
    loading() {
      return this.$store.state[types.COMMON].loading
    },
    loadingMessage() {
      return this.$store.state[types.COMMON].loadingMessage
    },
    error() {
      return this.$store.state[types.ERROR]
    },
    notificationList() {
      return this.$store.state[types.NOTIFICATIONS].list
    }
  },

  watch: {
    loading(value) {
      if (value) {
        if (this.loadingInstance) {
          this.loadingInstance.close()
        }

        this.loadingInstance = this.$loading({
          lock: true,
          fullscreen: true,
          text: this.loadingMessage
        })
      } else if (this.loadingInstance) {
        this.loadingInstance.close()
        this.loadingInstance = null
      }
    },
    '$i18n.locale': () => {
      this.setDocumentTitle()
    },
    error({ response }) {
      if (
        response
        && response.data
        && response.data.error === 'action_not_permitted'
      ) {
        this.createNotificationFromTemplate(templates.ACTION_NOT_PERMITTED)
      }
    },
    notificationList(value) {
      if (value.length > 0) this.shiftNotification()
    }
  },

  created() {
    this.setDocumentTitle()
  },

  methods: {
    setDocumentTitle(title = null) {
      const serviceName = this.$t('v.home.serviceName')

      if (document) {
        document.title = title || serviceName
      }
    },

    shiftNotification() {
      const notification = this.$store.state[types.NOTIFICATIONS].list[0]
      this.$store.commit(types.SHIFT_LIST, { name: types.NOTIFICATIONS })
      if ('template' in notification) {
        this.createNotificationFromTemplate(
          notification.template,
          notification
        )
      } else {
        this.createNotification(notification)
      }
    },

    createNotification({
      type = 'info', title, message, useHTML = false
    }) {
      setTimeout(() => {
        this.$notify({
          type,
          title,
          dangerouslyUseHTMLString: useHTML,
          message
        })
      }, 0)
    },

    createNotificationFromTemplate(template, payload) {
      if (
        [
          templates.PLACEMENTS_ADDED_TO_WHITELIST,
          templates.PLACEMENTS_ADDED_TO_BLACKLIST,
          templates.PLACEMENTS_REMOVED_FROM_WHITELIST,
          templates.PLACEMENTS_REMOVED_FROM_BLACKLIST
        ].includes(template)
      ) {
        const { list, count, logId } = payload

        // TODO: в [HMR] режиме отваливается $t (_t), проверить и подумать как можно исправить
        const moreLink = this.$tc('c.notification.placementsLinkToLog', count, [
          count,
          logId
        ])

        const notificationMap = {
          [templates.PLACEMENTS_ADDED_TO_WHITELIST]: this.$tc(
            'c.notification.placementsAddedToWhitelist',
            count
          ),
          [templates.PLACEMENTS_ADDED_TO_BLACKLIST]: this.$tc(
            'c.notification.placementsAddedToBlacklist',
            count
          ),
          [templates.PLACEMENTS_REMOVED_FROM_WHITELIST]: this.$tc(
            'c.notification.placementsRemovedFromWhitelist',
            count
          ),
          [templates.PLACEMENTS_REMOVED_FROM_BLACKLIST]: this.$tc(
            'c.notification.placementsRemovedFromBlacklist',
            count
          )
        }

        let message = notificationMap[template]
        message += `<br />${createHtmlMessageFromList(
          count > 3 ? [...list, '...'] : list
        )}`
        message += `<br />${moreLink}`

        const type = 'success'
        const title = this.$tc('c.notification.success')

        this.createNotification({
          type,
          title,
          message,
          useHTML: true
        })
        return
      }

      if (template === templates.PLACEMENTS_NOT_ADDED_TO_WHITELIST) {
        const { list } = payload

        let message = this.$tc('c.notification.placementsNotAddedToWhitelist')
        message += `<br />${createHtmlMessageFromList(list)}`

        const type = 'error'
        const title = this.$t('c.notification.error')

        this.createNotification({
          type,
          title,
          message,
          useHTML: true
        })
        return
      }

      if (
        template
        === templates.PLACEMENTS_NOT_ADDED_TO_BLACKLIST_BECAUSE_OF_LIMIT
      ) {
        const { excess } = payload

        const message = this.$tc(
          'c.notification.placementsNotAddedToBlacklistBecauseOfLimit',
          excess
        )

        const type = 'error'
        const title = this.$t('c.notification.error')

        this.createNotification({
          type,
          title,
          message,
          useHTML: true
        })
        return
      }

      if (
        [
          templates.PLACEMENTS_NOT_ADDED_TO_BLACKLIST_BECAUSE_OF_WHITELIST,
          templates.PLACEMENTS_NOT_ADDED_TO_BLACKLIST_BECAUSE_OF_BLACKLIST
        ].includes(template)
      ) {
        const { list } = payload

        const linksMap = {
          [templates.PLACEMENTS_NOT_ADDED_TO_BLACKLIST_BECAUSE_OF_WHITELIST]: this.$tc(
            'c.notification.removePlacementFromWhitelist',
            list.length,
            { link: '/white-list' }
          ),
          [templates.PLACEMENTS_NOT_ADDED_TO_BLACKLIST_BECAUSE_OF_BLACKLIST]: this.$tc(
            'c.notification.removePlacementFromBlacklist',
            list.length,
            { link: '/black-list' }
          )
        }

        const messagesMap = {
          [templates.PLACEMENTS_NOT_ADDED_TO_BLACKLIST_BECAUSE_OF_WHITELIST]: this.$tc(
            'c.notification.placementsNotAddedToBlacklistBecauseOfWhitelist',
            list.length
          ),
          [templates.PLACEMENTS_NOT_ADDED_TO_BLACKLIST_BECAUSE_OF_BLACKLIST]: this.$tc(
            'c.notification.placementsNotAddedToBlacklistBecauseOfBlacklist',
            list.length
          )
        }

        const link = linksMap[template]
        let message = messagesMap[template]
        message += `<br />${createHtmlMessageFromList(list)}`
        message += `<br />${link}`

        const type = 'error'
        const title = this.$t('c.notification.error')

        this.createNotification({
          type,
          title,
          message,
          useHTML: true
        })
        return
      }

      if (template === templates.ADS_CHANGED_STATUS) {
        const { list, status } = payload

        let message = this.$tc('c.notification.adsChangedStatus', list.length, {
          status
        })
        message += `<br />${createHtmlMessageFromList(list)}`
        const type = 'success'
        const title = this.$t('c.notification.success')

        this.createNotification({
          type,
          title,
          message,
          useHTML: true
        })
        return
      }

      if (
        [
          templates.ADS_ADDED_TO_EXCEPTIONS,
          templates.ADS_REMOVED_FROM_EXCEPTIONS,
          templates.ADS_ENABLED_AND_ADDED_TO_EXCEPTIONS
        ].includes(template)
      ) {
        const { list, count, logId } = payload

        const moreLink = this.$tc('c.notification.adsLinkToLog', count, [
          count,
          logId
        ])

        const notificationMap = {
          [templates.ADS_ADDED_TO_EXCEPTIONS]: this.$tc(
            'c.notification.adsAddedToExceptions',
            count
          ),
          [templates.ADS_REMOVED_FROM_EXCEPTIONS]: this.$tc(
            'c.notification.adsRemovedFromExceptions',
            count
          ),
          [templates.ADS_ENABLED_AND_ADDED_TO_EXCEPTIONS]: this.$tc(
            'c.notification.adsEnabledAndAddedToExceptions',
            count
          )
        }

        let message = notificationMap[template]
        message += `<br />${createHtmlMessageFromList(
          count > 3 ? [...list, '...'] : list
        )}`
        message += `<br />${moreLink}`

        const type = 'success'
        const title = this.$tc('c.notification.success')

        this.createNotification({
          type,
          title,
          message,
          useHTML: true
        })
        return
      }

      if (template === templates.KEYWORDS_CHANGED_STATUS) {
        const { list, status } = payload

        let message = this.$tc(
          'c.notification.keywordsChangedStatus',
          list.length,
          { status }
        )
        message += `<br />${createHtmlMessageFromList(list)}`
        const type = 'success'
        const title = this.$t('c.notification.success')

        this.createNotification({
          type,
          title,
          message,
          useHTML: true
        })
        return
      }

      if (
        [
          templates.KEYWORDS_ADDED_TO_EXCEPTIONS,
          templates.KEYWORDS_REMOVED_FROM_EXCEPTIONS,
          templates.KEYWORDS_ENABLED_AND_ADDED_TO_EXCEPTIONS
        ].includes(template)
      ) {
        const { list, count, logId } = payload

        const moreLink = this.$tc('c.notification.keywordsLinkToLog', count, [
          count,
          logId
        ])

        const notificationMap = {
          [templates.KEYWORDS_ADDED_TO_EXCEPTIONS]: this.$tc(
            'c.notification.keywordsAddedToExceptions',
            count
          ),
          [templates.KEYWORDS_REMOVED_FROM_EXCEPTIONS]: this.$tc(
            'c.notification.keywordsRemovedFromExceptions',
            count
          ),
          [templates.KEYWORDS_ENABLED_AND_ADDED_TO_EXCEPTIONS]: this.$tc(
            'c.notification.keywordsEnabledAndAddedToExceptions',
            count
          )
        }

        let message = notificationMap[template]
        message += `<br />${createHtmlMessageFromList(
          count > 3 ? [...list, '...'] : list
        )}`
        message += `<br />${moreLink}`

        const type = 'success'
        const title = this.$tc('c.notification.success')

        this.createNotification({
          type,
          title,
          message,
          useHTML: true
        })
        return
      }

      if (template === templates.ACTION_NOT_PERMITTED) {
        const type = 'error'
        const title = this.$t('c.notification.error')
        const message = this.$t('c.notification.actionNotPermitted')
        this.createDialogNotification(type, title, message)
        return
      }

      console.log('Error: Unknown template for notification message', template)
    },

    createDialogNotification(type, title, message) {
      this.dialogType = type
      this.dialogTitle = title
      this.dialogMessage = message
      this.$refs.notificationDialog.open()
    }
  }
}
</script>

<style lang="scss">
#app {
  min-height: 100vh;
}
</style>
